import React from "react";
import { useFormikContext } from "formik";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CheckboxField from "../../form/CheckboxField";
import { exclude } from "../../capture/ReceivingWizard/Items/useItemSchema";

const disabledFields = exclude.filter(field => field !== "item_id");

const styles = {
  headerCell: {
    fontWeight: "semibold",
    fontSize: 16,
    padding: '6px',
  },
  cell: {
    fontSize: 16,
    padding: '6px',
  },
  returnIcon: {
    mr: 1,
    transform: "rotate(180deg) scaleY(-1)",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
    ml: 1,
    "&::before": {
      content: '""',
      display: { xs: "none", md: "block" },
      width: 48,
      flexShrink: 0,
      height: 2,
      backgroundImage: theme =>
        `radial-gradient(circle, ${theme.palette.divider} 0.75px, transparent 1px)`,
      backgroundPosition: "center",
      backgroundSize: "10px 2px",
    },
  },
  checkbox: {
    p: 0,
  },
  fieldRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  fieldLabel: {
    display: "flex",
    alignItems: "center",
  },
  leader: {
    display: { xs: "none", md: "block" },
    flex: 1,
    mx: 2,
    height: 2,
    backgroundImage: theme =>
      `radial-gradient(circle, ${theme.palette.divider} 0.75px, transparent 1px)`,
    backgroundPosition: "center",
    backgroundSize: "10px 2px",
  },
};

export default function FieldsTable({ list, label, enabled, displayReceivingSettings = true }) {
  const { setFieldValue, values } = useFormikContext();

  if (!enabled || list.length === 0) {
    return null;
  }

  const handleInventoryDisplayChange = (name, checked) => {
    setFieldValue(`fieldSettings.${name}`, checked);
    if (displayReceivingSettings && !disabledFields.includes(name)) {
      setFieldValue(`receivingSettings.${name}`, checked);
    }
  };

  const handleRowClick = (event, name) => {
    if (event.target.closest("input, label, button")) return;

    handleInventoryDisplayChange(name, !values.fieldSettings?.[name]);
  };

  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell sx={styles.headerCell}>{label}</TableCell>
          <TableCell sx={styles.cell} />
          {displayReceivingSettings && <TableCell sx={styles.cell} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {list.map(([name, category, label]) => (
          <TableRow key={name} hover sx={styles.fieldRow} onClick={event => handleRowClick(event, name)}>
            <TableCell sx={styles.cell}>
              <Box sx={styles.fieldLabel}>
                <KeyboardReturnIcon htmlColor="#d5d5d5" sx={styles.returnIcon} />
                <Typography fontSize={16}>{label}</Typography>
                <Box sx={styles.leader} />
              </Box>
            </TableCell>
            <TableCell sx={styles.cell}>
              <Box sx={styles.checkboxContainer}>
                <CheckboxField
                  name={`fieldSettings.${name}`}
                  category={category}
                  label=""
                  sx={styles.checkbox}
                  onChange={e => handleInventoryDisplayChange(name, e.target.checked)}
                />
              </Box>
            </TableCell>
            {displayReceivingSettings && (
              <TableCell sx={styles.cell}>
                <Box sx={styles.checkboxContainer}>
                  {disabledFields.includes(name) ? (
                    <CheckboxField
                      name={`receivingSettings.${name}`}
                      category={category}
                      label=""
                      sx={styles.checkbox}
                      disabled
                      checked={false}
                    />
                  ) : (
                    <CheckboxField
                      name={`receivingSettings.${name}`}
                      category={category}
                      label=""
                      sx={styles.checkbox}
                    />
                  )}
                </Box>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}
